<template>
  <div
    id="app"
    class="h-100"
    :class="[skinClasses]"
  >
    <component :is="layout">
      <router-view />
    </component>

  </div>
</template>

<script>

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { watch } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import {
  // eslint-disable-next-line no-unused-vars
  isUserLoggedIn,
} from '@/auth/utils'

import { useWindowSize, useCssVar } from '@vueuse/core'

import store from '@/store'
import { mapState } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import useJwt from '@/auth/jwt/useJwt'

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

export default {
  components: {

    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,

  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    ...mapState('response', ['success', 'error']),
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
    usersData() {
      const users = this.$store.state.auth.userData
      if (users) {
        return users
      }
      return false
    },
  },
  watch: {
    error(value) {
      if (value) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'BellIcon',
            text: value.message,
            variant: 'danger',
          },
        })
      }
    },
    success(value) {
      if (value) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'BellIcon',
            text: value.message,
            variant: 'success',
          },
        })
      }
    },
  },
  async created() {
    const isLoggedIn = await isUserLoggedIn()
    if (isLoggedIn) {
      const user = await this.loadUser()
      const ability = user.permissions

      const userData = {
        ...user,
        role: user.roles,
        ability,
      }

      await this.$ability.update(ability)
      await this.$store.commit('auth/UPDATE_USER_DATA', userData)
      this.$nextTick(this.$forceUpdate)
    }
  },
  beforeCreate() {
    // auth token migration script
    // TODO: remove at early july 2025 to make sure all users have migrated to the new token system
    const oldToken = localStorage.getItem('accessToken')
    if (oldToken) {
      useJwt.setToken(oldToken)
      localStorage.removeItem('accessToken')
    }

    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  mounted() {
    let attempts = 0
    const maxAttempts = 50
    const interval = setInterval(() => {
      const iframe = document.querySelector('iframe')
      if (iframe && iframe.contentWindow) {
        setTimeout(() => {
          iframe.contentWindow.postMessage(
            { type: 'toggleDarkMode', isDark: this.skin === 'dark' },
            '*',
          )
        }, 1000)
        clearInterval(interval)
      }

      attempts += 1
      if (attempts >= maxAttempts) {
        clearInterval(interval)
      }
    }, 100)
  },
  setup() {
    const { skin, skinClasses } = useAppConfig()

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses,
    }
  },
  methods: {
    loadUser() {
      return this.$http.get('v1/admin/me')
        .then(response => response.data.data)
        .catch(() => null)
    },
  },
}
</script>
